"use client";

import React from "react";
import { motion } from "framer-motion";
import { MapPin, Users, Sparkles } from "lucide-react";
import { Image } from "@imagekit/next";

const folder = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER || "";

const steps = [
  {
    number: "01",
    icon: <MapPin className="w-6 h-6 text-[#FFAE00]" />,
    title: "Choose Your Trip",
    description:
      "Select an experience tailored to your passion — entrepreneurship, content creation, wellness, or more.",
    image: "Houseandmountain.JPG",
  },
  {
    number: "02",
    icon: <Users className="w-6 h-6 text-[#FFAE00]" />,
    title: "Travel with Mentors",
    description:
      "Join a curated group led by experts for hands-on sessions and meaningful outcomes.",
    image: "GroupDiscuss1.JPG",
  },
  {
    number: "03",
    icon: <Sparkles className="w-6 h-6 text-[#FFAE00]" />,
    title: "Leave with Clarity",
    description:
      "Depart with a clear roadmap, new connections, and lasting inspiration.",
    image: "clear2.JPG",
  },
];

const HowItWorksSection = () => {
  return (
    <section className="w-full px-6 sm:px-12 py-20 relative bg-[#FFFFFF] overflow-hidden">
      {/* Decorative Background Glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-50 rounded-full blur-[120px] -z-10 opacity-60" />
      
      <div className="max-w-7xl mx-auto">
        {/* Section Heading */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[#FFAE00] font-jakarta font-bold text-sm uppercase tracking-[3px]">The Process</span>
          <h2 className="font-bricolage font-bold text-[#1B1B1B] text-4xl sm:text-6xl mt-4">
            How It <span className="text-[#FFAE00]">Works</span>
          </h2>
          <p className="font-jakarta text-gray-500 mt-4 text-lg">3 simple steps to your next big breakthrough</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: Timeline & Content Cards */}
          <div className="w-full lg:w-1/2 space-y-8 relative">
            {/* The Connecting Dashed Line */}
            <div className="absolute left-[27px] top-10 bottom-10 w-[2px] border-l-2 border-dashed border-gray-100 -z-0" />

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: index * 0.2 }}
                viewport={{ once: true, margin: "-50px" }}
                className="relative flex items-start gap-6 group"
              >
                {/* Number Circle with Inner Shadow */}
                <div className="relative z-10 flex-shrink-0 w-14 h-14 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)] transition-all duration-300 group-hover:border-[#FFAE00]/30 group-hover:scale-110">
                  <span className="font-bricolage font-bold text-[#1B1B1B] text-lg">{step.number}</span>
                </div>

                {/* Content Card with Inner Shadow Effect */}
                <div className="flex-grow bg-white border border-gray-50 rounded-[24px] p-8 shadow-[inset_0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[inset_0_2px_15px_rgba(0,0,0,0.07)] transition-all duration-500 border-b-4 border-b-transparent hover:border-b-[#FFAE00]">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-2.5 bg-yellow-50 rounded-xl shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] transition-colors group-hover:bg-[#FFAE00]/10">
                      {step.icon}
                    </div>
                    <h3 className="font-bricolage font-bold text-[#1B1B1B] text-2xl tracking-tight">
                      {step.title}
                    </h3>
                  </div>
                  <p className="font-jakarta text-gray-500 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right: Premium Overlapping Image Display */}
          <div className="hidden lg:flex w-1/2 sticky top-24 h-[600px] items-center justify-center">
            <div className="relative w-full h-full max-w-sm">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, rotate: index % 2 === 0 ? -4 : 4 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: index === 1 ? 0 : (index === 0 ? -6 : 6) }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="absolute w-full aspect-[4/5] rounded-[32px] overflow-hidden border-[12px] border-white shadow-2xl transition-transform duration-500 hover:z-50 hover:scale-105 hover:rotate-0"
                  style={{
                    top: `${index * 40}px`,
                    left: `${index * 20}px`,
                    zIndex: 10 + index
                  }}
                >
                  <Image
                    src={`${folder}/${step.image}`}
                    alt={step.title}
                    fill
                    className="object-cover"
                    transformation={[{ width: 700, quality: 90, format: "webp" }]}
                  />
                  {/* Subtle Gradient Overlay for Text Readability if needed */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;